import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Tiny success-toast primitive. Auto-dismisses after `durationMs`
 * (default 5s). Render `<Toast message={message} />` once where the
 * toast should appear; call `show("Created!")` from a mutation
 * onSuccess.
 *
 * No global toast registry — each consumer scopes its own toast to a
 * surface. Keeps state local and avoids portal/provider plumbing for
 * the small number of toast surfaces in v1.
 */

const DEFAULT_DURATION_MS = 5000;

export interface UseToastApi {
  /** Current message, or null when dismissed. */
  message: string | null;
  /** Show a toast; auto-dismisses after `durationMs`. */
  show: (message: string) => void;
  /** Dismiss immediately (test hooks, manual dismiss). */
  dismiss: () => void;
}

export function useToast(durationMs: number = DEFAULT_DURATION_MS): UseToastApi {
  const [message, setMessage] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const dismiss = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setMessage(null);
  }, []);

  const show = useCallback(
    (next: string) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      setMessage(next);
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        setMessage(null);
      }, durationMs);
    },
    [durationMs],
  );

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return { message, show, dismiss };
}

export function Toast({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <p
      role="status"
      aria-live="polite"
      className="mt-3 rounded-md bg-[var(--color-success-soft)] px-3 py-2 text-sm text-[var(--color-success)]"
    >
      {message}
    </p>
  );
}
